import React, {
  useState,
  useEffect,
  useCallback,
  useRef
} from "react"
import { Modal } from "react-bootstrap"
import { useSelector } from "react-redux"
import { toast } from "react-toastify"
import { getBaseUrl, getCurrentUser } from "../../reducers"
import PdfCanvas from "./PdfCanvas"
import AnnotationToolbar from "./AnnotationToolbar"
import FlagCreationModal from "./FlagCreationModal"
import pdfExportService from "../../services/pdfExportService"
import styles from "./PdfToolbar.module.css"

const TOOL_SETTINGS_STORAGE_KEY = "pdfViewer.toolSettings.v1"
const DRAW_TOOL_ID = "draw"
const SIGNATURE_IMAGE_TOOL_ID = "signatureImage"

const defaultToolSettings = {
  text: {
    fontSize: 16,
    color: "#d32f2f",
    fontWeight: "normal",
    fontFamily: "Arial"
  },
  draw: {
    lineWidth: 2,
    color: "#0d47a1",
    lineStyle: "smooth"
  },
  highlight: {
    color: "#fff176",
    opacity: 0.5
  },
  line: {
    strokeColor: "#c62828",
    strokeWidth: 2
  },
  rectangle: {
    strokeColor: "#c62828",
    strokeWidth: 2,
    fillEnabled: false,
    fillColor: "#c62828",
    fillOpacity: 0.2
  },
  circle: {
    strokeColor: "#2e7d32",
    strokeWidth: 2,
    fillEnabled: false,
    fillColor: "#2e7d32",
    fillOpacity: 0.2
  },
  ellipse: {
    strokeColor: "#1565c0",
    strokeWidth: 2,
    fillEnabled: false,
    fillColor: "#1565c0",
    fillOpacity: 0.2
  }
}

const HISTORY_LIMIT = 50

const generateAnnotationId = () => `ann_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

const cloneAnnotations = (list = []) => list.map(annotation => {
  const clonedData = { ...(annotation.data || {}) }

  if (Array.isArray(clonedData.path)) {
    clonedData.path = clonedData.path.map(point => ({ ...point }))
  }

  return {
    ...annotation,
    data: clonedData
  }
})

const annotationsEqual = (left = [], right = []) => {
  if (left.length !== right.length) {
    return false
  }

  try {
    return JSON.stringify(left) === JSON.stringify(right)
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("Failed to compare annotations", err)
    return false
  }
}

export default function PdfViewer({
  uploadedFileId,
  show,
  onHide,
  title = "PDF Viewer",
  onUploadComplete = null,
  jobService = null,
  onFlagCreated = () => {}
}) {
  // eslint-disable-next-line no-console
  console.log("PdfViewer render:", { uploadedFileId, show })

  const [pdfViewerData, setPdfViewerData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [currentTool, setCurrentTool] = useState("draw")
  const [currentPage, setCurrentPage] = useState(1)
  const [annotations, setAnnotations] = useState([])
  const [saving, setSaving] = useState(false)
  const [selectedStampType, setSelectedStampType] = useState("approved")
  const [exporting, setExporting] = useState(false)
  const [flagExporting, setFlagExporting] = useState(false)
  const [toolSettings, setToolSettings] = useState(() => {
    if (typeof window === "undefined") {
      return defaultToolSettings
    }

    try {
      const stored = window.localStorage.getItem(TOOL_SETTINGS_STORAGE_KEY)
      if (stored) {
        const parsed = JSON.parse(stored)
        if (parsed.signature && !parsed.draw) {
          parsed.draw = parsed.signature
        }
        return Object.keys(defaultToolSettings).reduce((acc, key) => ({
          ...acc,
          [key]: {
            ...defaultToolSettings[key],
            ...(parsed[key] || {})
          }
        }), {})
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("Failed to load PDF tool settings, using defaults", err)
    }

    return defaultToolSettings
  })
  const [selectedAnnotationId, setSelectedAnnotationId] = useState(null)
  const historyRef = useRef({ past: [], future: [] })
  const [showFlagModal, setShowFlagModal] = useState(false)
  const [exportedPdfForFlag, setExportedPdfForFlag] = useState(null)
  const [exportedPdfFilename, setExportedPdfFilename] = useState("")
  const baseUrl = useSelector(getBaseUrl)
  const currentUser = useSelector(getCurrentUser)
  const [userSignatureAsset, setUserSignatureAsset] = useState({
    url: null,
    naturalWidth: 0,
    naturalHeight: 0,
    ready: false
  })
  const jobServiceRecord = jobService?.data || jobService || null
  const jobServiceAttributes = jobServiceRecord?.attributes || null
  const jobServiceContext = pdfViewerData?.job_service_context
  const jobServiceId = jobServiceRecord?.id
    || jobServiceContext?.id
    || jobServiceContext?.job_service_id
    || null
  const jobServiceState = jobServiceAttributes?.state || jobServiceContext?.state || null
  const signatureImagePath = currentUser?.attributes?.surveyor_signature_path
    || currentUser?.surveyor_signature_path
    || null
  const signatureToolEnabled = Boolean(userSignatureAsset.ready)

  useEffect(() => {
    let isMounted = true
    if (!signatureImagePath) {
      setUserSignatureAsset({
        url: null,
        naturalWidth: 0,
        naturalHeight: 0,
        ready: false
      })
      return () => {
        isMounted = false
      }
    }

    if (typeof window === "undefined") {
      return () => {
        isMounted = false
      }
    }

    const image = new window.Image()
    image.crossOrigin = "anonymous"
    image.onload = () => {
      if (!isMounted) return
      setUserSignatureAsset({
        url: signatureImagePath,
        naturalWidth: image.naturalWidth || 0,
        naturalHeight: image.naturalHeight || 0,
        ready: true
      })
    }
    image.onerror = () => {
      if (!isMounted) return
      setUserSignatureAsset({
        url: signatureImagePath,
        naturalWidth: 0,
        naturalHeight: 0,
        ready: false
      })
      toast.error("Unable to load your saved signature. Please re-upload it in Account Settings.")
    }
    image.src = signatureImagePath

    return () => {
      isMounted = false
    }
  }, [signatureImagePath])

  useEffect(() => {
    if (typeof window === "undefined") return

    try {
      window.localStorage.setItem(TOOL_SETTINGS_STORAGE_KEY, JSON.stringify(toolSettings))
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("Failed to persist PDF tool settings", err)
    }
  }, [toolSettings])

  const handleToolSettingsChange = useCallback((toolKey, updates) => {
    if (!toolKey) return

    setToolSettings(prev => {
      const existing = prev[toolKey] || {}
      return {
        ...prev,
        [toolKey]: {
          ...existing,
          ...updates
        }
      }
    })
  }, [])

  const handleToolChange = useCallback(toolId => {
    setCurrentTool(toolId)
    if (toolId !== "select") {
      setSelectedAnnotationId(null)
    }
  }, [])

  const applyAnnotationsUpdate = useCallback((producer, options = {}) => {
    const { recordHistory = true } = options

    const previous = cloneAnnotations(annotations)
    const workingCopy = cloneAnnotations(previous)
    const produced = typeof producer === "function"
      ? producer(workingCopy)
      : producer
    const nextSnapshot = cloneAnnotations(produced || [])

    if (annotationsEqual(previous, nextSnapshot)) {
      return
    }

    if (recordHistory) {
      const past = [...historyRef.current.past, previous]
      if (past.length > HISTORY_LIMIT) {
        past.shift()
      }

      historyRef.current = {
        past,
        future: []
      }
    }

    setAnnotations(nextSnapshot)
  }, [annotations])

  const fetchPdfViewerData = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      // Check if authentication token is available
      if (!gon || !gon.auth_token) {
        throw new Error("Authentication token is missing. Please refresh the page and sign in again.")
      }

      const response = await fetch(`${baseUrl}/uploads/${uploadedFileId}/pdf-viewer`, {
        headers: {
          Authorization: gon.auth_token,
          "Content-Type": "application/json"
        }
      })

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error("You do not have permission to view this PDF file.")
        }
        if (response.status === 400) {
          throw new Error("This file is not a PDF or cannot be viewed.")
        }
        if (response.status === 404) {
          throw new Error("PDF file not found or not available.")
        }
        throw new Error(`Failed to load PDF: ${response.statusText}`)
      }

      // Check if response is JSON
      const contentType = response.headers.get("content-type")
      if (!contentType || !contentType.includes("application/json")) {
        throw new Error("Server returned an invalid response. You may need to refresh the page and sign in again.")
      }

      let data = await response.json()

      // Handle Grape API response wrapper (data might be nested under "json" key)
      if (data.json && typeof data.json === "object") {
        data = data.json
      }

      // Validate the response structure
      if (!data || !data.file_info) {
        throw new Error("Server returned invalid PDF viewer data.")
      }

      // Check if PDF URL is available
      if (!data.pdf_url) {
        throw new Error("PDF file URL is not available for viewing.")
      }

      setPdfViewerData(data)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [baseUrl, uploadedFileId])

  useEffect(() => {
    if (show && uploadedFileId) {
      fetchPdfViewerData()
    }
  }, [show, uploadedFileId, fetchPdfViewerData])

  const resetFlagState = useCallback(() => {
    setShowFlagModal(false)
    setExportedPdfForFlag(null)
    setExportedPdfFilename("")
    setFlagExporting(false)
  }, [])

  const handleClose = () => {
    resetFlagState()
    setPdfViewerData(null)
    setError(null)
    historyRef.current = { past: [], future: [] }
    setAnnotations([])
    setSelectedAnnotationId(null)
    onHide()
  }

  const handleFlagModalHide = useCallback(() => {
    resetFlagState()
  }, [resetFlagState])

  const handleFlagCreationSuccess = useCallback(() => {
    if (typeof onFlagCreated === "function") {
      onFlagCreated()
    }
    handleClose()
  }, [onFlagCreated, handleClose])

  // Handle annotation changes
  const handleAnnotationChange = useCallback(annotationData => {
    applyAnnotationsUpdate(() => annotationData)
  }, [applyAnnotationsUpdate])

  const handleSelectionChange = useCallback(annotationId => {
    setSelectedAnnotationId(annotationId || null)
  }, [])

  const handleDeleteSelected = useCallback(() => {
    if (!selectedAnnotationId) {
      return
    }

    applyAnnotationsUpdate(prev => prev.filter(annotation => annotation.id !== selectedAnnotationId))
    setSelectedAnnotationId(null)
  }, [selectedAnnotationId, applyAnnotationsUpdate])

  const handleDuplicateSelected = useCallback(() => {
    if (!selectedAnnotationId) {
      return
    }

    const source = annotations.find(annotation => annotation.id === selectedAnnotationId)
    if (!source) {
      return
    }

    const duplicate = cloneAnnotations([source])[0]
    duplicate.id = generateAnnotationId()

    if (typeof duplicate.data.x === "number") {
      duplicate.data.x += 12
    }
    if (typeof duplicate.data.y === "number") {
      duplicate.data.y += 12
    }

    if (Array.isArray(duplicate.data.path)) {
      duplicate.data.path = duplicate.data.path.map(point => ({
        x: point.x + 12,
        y: point.y + 12
      }))
    }

    applyAnnotationsUpdate(prev => [...prev, duplicate])
    setSelectedAnnotationId(duplicate.id)
  }, [annotations, selectedAnnotationId, applyAnnotationsUpdate])

  const handleUndo = useCallback(() => {
    const { past, future } = historyRef.current
    if (!past.length) {
      return
    }

    const previous = past[past.length - 1]
    historyRef.current = {
      past: past.slice(0, -1),
      future: [cloneAnnotations(annotations), ...future]
    }

    setAnnotations(cloneAnnotations(previous))
    setSelectedAnnotationId(null)
  }, [annotations])

  const handleRedo = useCallback(() => {
    const { past, future } = historyRef.current
    if (!future.length) {
      return
    }

    const next = future[0]
    historyRef.current = {
      past: [...past, cloneAnnotations(annotations)],
      future: future.slice(1)
    }

    setAnnotations(cloneAnnotations(next))
    setSelectedAnnotationId(null)
  }, [annotations])

  useEffect(() => {
    if (selectedAnnotationId && !annotations.some(annotation => annotation.id === selectedAnnotationId)) {
      setSelectedAnnotationId(null)
    }
  }, [annotations, selectedAnnotationId])

  // Handle save annotations
  const handleSaveAnnotations = useCallback(async () => {
    if (!pdfViewerData) return

    if (annotations.length === 0) {
      setError("No annotations to save")
      return
    }

    setSaving(true)
    try {
      const payload = {
        uploaded_file_id: uploadedFileId,
        annotations: annotations.map(a => ({
          annotation_type: a.type,
          page_number: a.page,
          annotation_data: a.data
        }))
      }

      // eslint-disable-next-line no-console
      console.log("Saving annotations:", payload)

      const response = await fetch(`${baseUrl}/pdf_annotations`, {
        method: "POST",
        headers: {
          Authorization: gon.auth_token,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(payload)
      })

      if (!response.ok) {
        const errorText = await response.text()
        // eslint-disable-next-line no-console
        console.error("Save failed:", response.status, errorText)
        throw new Error(`Save failed: ${response.statusText}`)
      }

      const result = await response.json()
      setSaving(false)

      // Update annotations with server IDs
      if (result.data) {
        const updatedAnnotations = result.data.map((ann, index) => ({
          ...annotations[index],
          id: ann.id
        }))
        setAnnotations(updatedAnnotations)
      }

      // eslint-disable-next-line no-alert
      alert("Annotations saved successfully!")
    } catch (err) {
      setSaving(false)
      setError(`Failed to save annotations: ${err.message}`)
    }
  }, [baseUrl, uploadedFileId, pdfViewerData, annotations])

  const generateExportedPdfFile = useCallback(async () => {
    if (!pdfViewerData || annotations.length === 0) {
      throw new Error("No annotations to export")
    }

    const pdfUrl = `${baseUrl}/uploads/${uploadedFileId}/stream`
    const originalFilename = pdfViewerData.file_info?.name || "document.pdf"

    const exportedBlob = await pdfExportService.exportPdfWithAnnotations(
      pdfUrl,
      annotations,
      originalFilename,
      1.5
    )

    const exportFilename = pdfExportService.generateExportFilename(originalFilename)
    const file = new File([exportedBlob], exportFilename, { type: "application/pdf" })

    return { file, exportFilename }
  }, [baseUrl, uploadedFileId, pdfViewerData, annotations])

  // Handle export PDF with annotations
  const handleExportPdf = useCallback(async () => {
    if (!pdfViewerData || annotations.length === 0) {
      toast.error("No annotations to export")
      return
    }

    setExporting(true)
    try {
      const { file } = await generateExportedPdfFile()

      toast.success("PDF exported! Opening upload dialog...")

      handleClose()

      if (onUploadComplete) {
        onUploadComplete(file)
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Export failed:", err)
      toast.error(`Export failed: ${err.message}`)
    } finally {
      setExporting(false)
    }
  }, [pdfViewerData, annotations, generateExportedPdfFile, handleClose, onUploadComplete])

  const handleExportPdfAndFlag = useCallback(async () => {
    if (!jobServiceId) {
      toast.error("Unable to create flag: missing job service context.")
      return
    }

    if (!pdfViewerData || annotations.length === 0) {
      toast.error("No annotations to export")
      return
    }

    setFlagExporting(true)
    try {
      const { file, exportFilename } = await generateExportedPdfFile()
      setExportedPdfForFlag(file)
      setExportedPdfFilename(exportFilename)
      setShowFlagModal(true)
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Export failed:", err)
      toast.error(`Export failed: ${err.message}`)
    } finally {
      setFlagExporting(false)
    }
  }, [jobServiceId, pdfViewerData, annotations, generateExportedPdfFile])

  useEffect(() => {
    if (!show) {
      return undefined
    }

    const handleKeyDown = event => {
      if (!show) {
        return
      }

      const { target } = event
      const tagName = target && target.tagName ? target.tagName.toLowerCase() : ""

      if (tagName === "input" || tagName === "textarea" || (target && target.isContentEditable)) {
        return
      }

      if ((event.ctrlKey || event.metaKey) && !event.altKey) {
        const key = event.key.toLowerCase()

        if (key === "z") {
          event.preventDefault()
          if (event.shiftKey) {
            handleRedo()
          } else {
            handleUndo()
          }
          return
        }

        if (key === "y") {
          event.preventDefault()
          handleRedo()
          return
        }

        if (key === "d") {
          if (selectedAnnotationId) {
            event.preventDefault()
            handleDuplicateSelected()
          }
          return
        }

        if (key === "s") {
          event.preventDefault()
          handleExportPdf()
          return
        }
      }

      if (event.key === "Escape") {
        handleToolChange("select")
        handleSelectionChange(null)
        event.preventDefault()
        return
      }

      if (!event.ctrlKey && !event.metaKey && !event.altKey) {
        const key = event.key.toLowerCase()

        if (key === "h") {
          handleToolChange("highlight")
          event.preventDefault()
          return
        }

        if (key === "t") {
          handleToolChange("text")
          event.preventDefault()
          return
        }

        if (key === "d") {
          handleToolChange(DRAW_TOOL_ID)
          event.preventDefault()
          return
        }

        if (key === "g" && signatureToolEnabled) {
          handleToolChange(SIGNATURE_IMAGE_TOOL_ID)
          event.preventDefault()
          return
        }
      }

      if ((event.key === "Delete" || event.key === "Backspace") && selectedAnnotationId) {
        event.preventDefault()
        handleDeleteSelected()
      }
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => {
      window.removeEventListener("keydown", handleKeyDown)
    }
  }, [
    show,
    handleToolChange,
    handleSelectionChange,
    handleUndo,
    handleRedo,
    handleDeleteSelected,
    handleDuplicateSelected,
    handleExportPdf,
    selectedAnnotationId,
    signatureToolEnabled
  ])

  // Load annotations when PDF data is loaded
  useEffect(() => {
    if (pdfViewerData && pdfViewerData.annotations) {
      const annotationData = pdfViewerData.annotations.data || []

      // Convert backend format to frontend format
      const loadedAnnotations = annotationData.map(ann => ({
        id: ann.id,
        type: ann.annotation_type,
        page: ann.page_number,
        data: ann.annotation_data
      }))

      // eslint-disable-next-line no-console
      console.log("Loaded annotations:", loadedAnnotations)
      historyRef.current = { past: [], future: [] }
      setSelectedAnnotationId(null)
      setAnnotations(cloneAnnotations(loadedAnnotations))
    } else if (!pdfViewerData) {
      historyRef.current = { past: [], future: [] }
      setSelectedAnnotationId(null)
      setAnnotations([])
    }
  }, [pdfViewerData])

  const canUndo = historyRef.current.past.length > 0
  const canRedo = historyRef.current.future.length > 0
  const canModifySelection = Boolean(selectedAnnotationId)
  const canShowFlagAction = Boolean(
    annotations.length > 0
    && jobServiceId
    && jobServiceState === "to_review"
    && (pdfViewerData?.permissions?.can_flag || pdfViewerData?.permissions?.can_export)
  )

  if (!show) return null

  return (
    <>
      <Modal
        show={show}
        onHide={handleClose}
        size="xl"
        className="pdf-viewer-modal"
        centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {title}
            {pdfViewerData && pdfViewerData.file_info && (
              <small className="text-muted ml-2">
                {pdfViewerData.file_info.name}
              </small>
            )}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className={`pdf-viewer-body ${styles.pdfViewerBody}`}>
          {loading && (
          <div className="d-flex justify-content-center align-items-center" style={{ height: "400px" }}>
            <div className="spinner-border" role="status">
              <span className="sr-only">Loading PDF...</span>
            </div>
          </div>
        )}

          {error && (
          <div className="alert alert-danger" role="alert">
            <h5>Error Loading PDF</h5>
            <p>{error}</p>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={fetchPdfViewerData}>
              Try Again
            </button>
          </div>
        )}

          {pdfViewerData && !loading && !error && (
          <div className="pdf-viewer-container">
            <div className="pdf-info-header mb-3">
              <div className="row">
                <div className="col-md-8">
                  <h6 className="mb-1">File Information</h6>
                  <small className="text-muted">
                    Category:
                    {" "}
                    {pdfViewerData.file_info.document_category}
                  </small>
                  <br />
                  <small className="text-muted">
                    Type:
                    {" "}
                    {pdfViewerData.file_info.uploadable_type}
                  </small>
                  {pdfViewerData.file_info.ready_for_release && (
                    <span className="badge badge-warning ml-2">Ready for Release</span>
                  )}
                </div>
                <div className="col-md-4 text-right">
                  <small className="text-muted">
                    Annotations:
                    {" "}
                    {pdfViewerData.annotations.user_visible_count}
                    {" "}
                    visible
                  </small>
                </div>
              </div>
            </div>

            {/* Annotation Toolbar */}
            <AnnotationToolbar
              currentTool={currentTool}
              onToolChange={handleToolChange}
              canAnnotate={pdfViewerData.permissions.can_annotate}
              onSave={handleSaveAnnotations}
              saving={saving}
              selectedStampType={selectedStampType}
              onStampTypeChange={setSelectedStampType}
              onUndo={handleUndo}
              onRedo={handleRedo}
              canUndo={canUndo}
              canRedo={canRedo}
              onDelete={handleDeleteSelected}
              onDuplicate={handleDuplicateSelected}
              canDelete={canModifySelection}
              canDuplicate={canModifySelection}
              toolSettings={toolSettings}
              onToolSettingsChange={handleToolSettingsChange}
              signatureToolEnabled={signatureToolEnabled}
              onExport={handleExportPdf}
              exporting={exporting}
              canExport={pdfViewerData.permissions.can_export && annotations.length > 0}
              onExportAndFlag={handleExportPdfAndFlag}
              flagExporting={flagExporting}
              canShowFlagAction={canShowFlagAction && annotations.length > 0} />

            {/* PDF Canvas */}
            <PdfCanvas
              pdfUrl={`${baseUrl}/uploads/${uploadedFileId}/stream`}
              annotations={annotations}
              onAnnotationChange={handleAnnotationChange}
              canAnnotate={pdfViewerData.permissions.can_annotate}
              currentTool={currentTool}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
              selectedStampType={selectedStampType}
              toolSettings={toolSettings}
              selectedAnnotationId={selectedAnnotationId}
              onSelectionChange={handleSelectionChange}
              userSignatureAsset={userSignatureAsset}
              userInfo={{
                firstName: currentUser?.attributes?.firstname || "",
                surname: currentUser?.attributes?.surname || "",
                role: currentUser?.attributes?.default_role || currentUser?.attributes?.role || "",
                surveyorId: currentUser?.attributes?.surveyor_id || "",
                signaturePath: signatureImagePath
              }} />

            <div className="pdf-permissions-info mt-3">
              <small className="text-muted">
                <strong>Your permissions:</strong>
                {pdfViewerData.permissions.can_annotate
                  ? <span className="text-success"> Can annotate</span>
                  : <span className="text-warning"> View only</span>}
                {pdfViewerData.permissions.can_export
                  && <span className="text-info"> • Can export</span>}
              </small>
            </div>
          </div>
        )}
        </Modal.Body>

        {pdfViewerData && (
        <Modal.Footer className="pdf-viewer-footer">
          <div className="w-100 d-flex justify-content-end">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleClose}>
              Close
            </button>
          </div>
        </Modal.Footer>
      )}
      </Modal>

      <FlagCreationModal
        show={showFlagModal}
        onHide={handleFlagModalHide}
        jobServiceId={jobServiceId}
        jobServiceState={jobServiceState}
        attachedFile={exportedPdfForFlag}
        attachedFileName={exportedPdfFilename}
        onSuccess={handleFlagCreationSuccess} />
    </>
  )
}
